import React, { createContext, useContext, useEffect, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import { trpc } from "./trpc";
import { setSentryUser } from "./sentry";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface User {
  id: number;
  email: string;
  fullName: string;
  role: "user" | "admin" | "owner";
  avatarUrl?: string | null;
  hasPassword: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string, currentPassword?: string) => Promise<void>;
  refetchUser: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const meQuery = trpc.auth.me.useQuery(undefined, {
    retry: false,
    enabled: true,
  });

  const loginMutation = trpc.auth.login.useMutation();
  const logoutMutation = trpc.auth.logout.useMutation();

  useEffect(() => {
    const raw = meQuery.data?.user as
      | (Omit<User, "fullName"> & { name?: string | null; fullName?: string | null; hasPassword?: boolean })
      | null
      | undefined;
    if (raw) {
      const next = {
        id: raw.id,
        email: raw.email,
        fullName: raw.fullName || raw.name || (raw.email ? raw.email.split("@")[0] : "User"),
        role: raw.role,
        avatarUrl: raw.avatarUrl ?? null,
        hasPassword: raw.hasPassword ?? true,
      };
      setUser(next);
      setSentryUser({ id: next.id, email: next.email });
    } else if (meQuery.isError || meQuery.data?.user === null) {
      setUser(null);
      setSentryUser(null);
    }
    if (!meQuery.isLoading) {
      setLoading(false);
    }
  }, [meQuery.data, meQuery.isError, meQuery.isLoading]);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data.session) {
      await loginMutation.mutateAsync({ accessToken: data.session.access_token });
      meQuery.refetch();
    }
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    await logoutMutation.mutateAsync();
    await supabase.auth.signOut();
    setUser(null);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/profile?reset=1`,
    });
    if (error) throw error;
  };

  const updatePassword = async (newPassword: string, currentPassword?: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
      ...(currentPassword ? { nonce: currentPassword } : {}),
    });
    if (error) throw error;
  };

  // Handle OAuth callback and password recovery
  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" && session) {
        try {
          await loginMutation.mutateAsync({ accessToken: session.access_token });
          meQuery.refetch();
        } catch {
          // Login mutation may fail if user is not invited
        }
      }
      if (event === "PASSWORD_RECOVERY" && session) {
        // User clicked password reset link — sign them in so they can set a new password
        try {
          await loginMutation.mutateAsync({ accessToken: session.access_token });
          meQuery.refetch();
          // Redirect to profile page to set new password
          window.location.href = "/profile?reset=1";
        } catch {
          // May fail if user not in CRM yet
        }
      }
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signIn,
        signInWithGoogle,
        signUp,
        signOut,
        resetPassword,
        updatePassword,
        refetchUser: () => meQuery.refetch(),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
